<!--
 @description Language-switcher button that cycles through available vue-i18n
 locales. Renders one button per locale that is not currently active. Persists
 the selected locale to localStorage and redirects unauthenticated users to
 the home route. Designed for placement inside a navbar or settings panel.

 @example
 <Locales />

 @accessibility The button carries a static `aria-label="Change Locale"`.
 The visible label is the locale code (e.g. "fr", "en") — ensure the
 surrounding context makes the purpose clear to screen-reader users.
-->
<template>
  <button
    class="no-style-button locales"
    v-for="locale in $i18n.availableLocales"
    :key="locale"
    v-show="locale !== $i18n.locale"
    @click="changeLocale"
    aria-label="Change Locale"
  >
    <span>
      {{ locale }}
    </span>
  </button>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { User } from '@/types/Types'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'

const authStore = useAuthStore()
const { locale } = useI18n()
const currentUser = ref()
const user = ref<User | null>()
const router = useRouter()

function changeLocale(event: MouseEvent) {
  const target = event.target as HTMLElement
  const spanElement = target.querySelector('span')
  if (spanElement) {
    const range = document.createRange()
    range.selectNodeContents(spanElement)

    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  const newLocale = target.innerText.toLowerCase()
  locale.value = newLocale
  localStorage.setItem('locale', newLocale)
}

function getLocale() {
  if (user.value?.preferredLanguage) {
    locale.value = user.value.preferredLanguage
    localStorage.setItem('locale', locale.value)
  } else {
    const localLocale = localStorage.getItem('locale')
    if (localLocale) locale.value = localLocale
  }
}

async function validateUserIsSignedIn() {
  if (authStore.firebaseUser) {
    currentUser.value = authStore.firebaseUser
  } else {
    router.push({ name: 'home' })
  }
}

onMounted(async () => {
  await validateUserIsSignedIn()
  getLocale()
})
</script>

<style scoped lang="scss">
.locales {
  width: 149px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  text-align: end;

  @include breakpoint-md {
    margin-right: 1rem;
    width: 100%;
  }
}
</style>
